import {
  Divider,
  Flex,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  Typography,
} from "antd";

import "./ProjectContactInformation.scss";
import { ProjectFormFieldItem } from "../project-form-field-item/ProjectFormFieldItem";
import { useDisplayFieldByStage } from "projects/hooks";
import { UsStates } from "projects/constants";
import { PhoneMaskedInput } from "shared/components";

const fieldCategory = "contactInformationFields";

type Props = {
  form: FormInstance;
  currentStage: string;
  isEditing: boolean;
};

export const ProjectContactInformation = ({
  form,
  currentStage,
}: Props) => {
  const validationStage = Form.useWatch("stage", form);
  const hasStageChanged = form.isFieldTouched("stage");
  const { displayFieldCategoryByStage } = useDisplayFieldByStage();

  return displayFieldCategoryByStage(fieldCategory, currentStage) ? (
    <div className='project-contact-information-container'>
      <Flex>
        <Typography.Title
          className='project-contact-information-title'
          level={2}
        >
          Contact Information
        </Typography.Title>
      </Flex>
      <Divider className='project-contact-information-divider' />
      <Row justify='start' gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
        <ProjectFormFieldItem
          name='address'
          label='Address'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
        >
          <Input size='small' />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='ownerName'
          label='Owner Name'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
        >
          <Input size='small' />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='ownerEmail'
          label='Owner Email'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          rules={[
            {
              type: "email",
              message: "Please enter a valid email address.",
            },
          ]}
        >
          <Input size='small' type='email' />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='ownerPhone'
          label='Owner Phone'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          rules={[
            {
              validator: (_: any, value: string) => {
                if (!value) return Promise.resolve();
                const digits = value.replace(/\D/g, "");
                if (digits.length === 10) return Promise.resolve();
                return Promise.reject("Please enter a valid US phone number (e.g., (123) 456-7890)");
              },
            },
          ]}
        >
          <PhoneMaskedInput size='small' />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='city'
          label='City'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
        >
          <Input size='small' />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='state'
          label='State'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
        >
          <Select
            size='small'
            placeholder='Select...'
            showSearch
            filterOption={(input, option) =>
              (option?.label as string ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={UsStates}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='zip'
          label='Zip'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          rules={[
            {
              pattern: /^\d{5}(-\d{4})?$/,
              message: "Please enter a valid zip code (e.g., 12345 or 12345-6789).",
            },
          ]}
        >
          <Input size='small' />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='onsiteAuditContact'
          label='Onsite Audit Contact'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
        >
          <Input size='small' />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='onsiteEmail'
          label='Onsite Email'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          rules={[
            {
              type: "email",
              message: "Please enter a valid email address.",
            },
          ]}
        >
          <Input size='small' type='email' />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='onsitePhone'
          label='Onsite Phone'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          rules={[
            {
              validator: (_: any, value: string) => {
                if (!value) return Promise.resolve();
                const digits = value.replace(/\D/g, "");
                if (digits.length === 10) return Promise.resolve();
                return Promise.reject("Please enter a valid US phone number (e.g., (123) 456-7890)");
              },
            },
          ]}
        >
          <PhoneMaskedInput size='small' />
        </ProjectFormFieldItem>
      </Row>
    </div>
  ) : null;
};
